import { signIn, signOut, useSession } from 'next-auth/react';
import { useUserContext } from '../contexts/UserContext';
import { userLogout } from '../helpers/Logout';
import { useRouter } from 'next/router';
import axios from 'axios';
import { fireToast } from '../helpers/Toasts';
import { randomStringGenerator } from '../helpers/randomStringGenerator';
import { generateDDRecommendedUserCookie } from '../utils/ddRecommendedUserCookie';
import { User } from '../types';
import { API } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

export const keycloakLogout = async (callbackUrl?: string) => {
  try {
    await axios.get('/api/auth/keycloak_signout');
    await signOut(callbackUrl ? { callbackUrl } : { redirect: false });
  } catch (error) {
    logger.error({
      message: 'Unable to signout',
      error,
      route: API.KEYCLOAK_SIGNOUT,
    });
  }
};

const useAuthOptions = () => {
  const {
    /**
     * Legacy authentication relied on cookies to store user data and authenticate requests.
     *
     * This cookie is still required for those pages that do not use Keycloak for user information
     * that is not being derived from the session.
     *
     * Once all of data is derived from the session rather than the cookie, references to this cookie
     * may be removed from this hook.
     */
    cookieBasedUser,
    dispatchCookieBasedUser,
    consentOptions: { functionalConsent },
  } = useUserContext();
  const { data: session, status } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const cookieBasedUsername = cookieBasedUser?.name || '';
  const isLoggedInKeycloakUser = !!session?.user;
  const shouldReturnAccessToken = !!session?.user && !session?.error;

  const handleLogin = (url?: string) => {
    signIn('keycloak', {
      callbackUrl:
        typeof url === 'string'
          ? `/signin?callbackUrl=${url}`
          : `/signin?callbackUrl=${asPath}`,
    });
  };

  const handleLogout = async (callbackUrl?: string, user?: User) => {
    try {
      await keycloakLogout(callbackUrl);
    } catch (error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return;
    }
    if (functionalConsent) {
      document.cookie = generateDDRecommendedUserCookie(
        randomStringGenerator(),
      );
    }

    /**
     * This cookie is the only method of authentication on C2B pages for users with dealer accounts.
     *
     * However, if one of these users visits a marketplace page, we log them out immediately to avoid using state derived
     * from this cookie. Cookies with dealer user data are for C2B pages only and never marketplace pages
     */
    if (!user || user?.type !== 'DEALER') {
      await userLogout(dispatchCookieBasedUser, cookieBasedUsername);
    }
  };

  return {
    handleLogin,
    handleLogout,
    isAuthenticated: isLoggedInKeycloakUser,
    accessToken: shouldReturnAccessToken ? session?.accessToken : undefined,
    status,
    user: session?.user,
  };
};

export { useAuthOptions };
